import { none, Option, some } from 'fp-ts/lib/Option';
import * as t from 'io-ts';
import { useCallback, useEffect, useState } from 'react';
import { getJSON, setJSON } from './sessionStorage';
import storageAvailable from './storageAvailable';

const STORAGE_KEY = 'jg-search-scroll-position';

// Structure of the data that will be stored in sessionStorage
const StorageData = t.type({
  url: t.string,
  scrollY: t.number,
});
type StorageData = t.TypeOf<typeof StorageData>;

const sessionStorageAvailable = storageAvailable('sessionStorage');

if (sessionStorageAvailable && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

// Global flag to indicate that the user has performed a scroll event
let userHasScrolled = false;

// Install this ASAP, don’t wait for the React app to mount
// One-time event listener to set the `userHasScrolled` flag
const onScroll = () => {
  userHasScrolled = true;
  window.removeEventListener('scroll', onScroll);
};
window.addEventListener('scroll', onScroll);

export default function useScrollRestoration(): { restoreScroll: () => void } {
  useSaveScrollPositionOnUnload();

  const previousScrollPosition = usePreviousScrollPosition();
  const [hasRestored, setHasRestored] = useState(false);

  const restoreScroll = useCallback(() => {
    if (previousScrollPosition.isNone() || userHasScrolled || hasRestored) {
      return;
    }
    window.scrollTo(0, previousScrollPosition.value);
    setHasRestored(true);
  }, [previousScrollPosition, hasRestored]);

  return { restoreScroll };
}

function usePreviousScrollPosition() {
  const [previousScrollY, setPreviousScrollY] = useState<Option<number>>(none);

  useEffect(() => {
    const scrollY = getJSON(STORAGE_KEY)
      .chain(data => StorageData.decode(data))
      .filterOrElse(
        data => data.url === window.location.href,
        'URL in storage does not match current URL'
      )
      .map(data => data.scrollY);

    if (scrollY.isRight()) {
      setPreviousScrollY(some(scrollY.value));
    }
  }, []);

  return previousScrollY;
}

function useSaveScrollPositionOnUnload() {
  useEffect(() => {
    if (!sessionStorageAvailable) {
      return;
    }

    function onUnload() {
      const storageData: StorageData = {
        url: window.location.href,
        scrollY: window.scrollY,
      };
      setJSON(STORAGE_KEY, storageData);
    }

    window.addEventListener('unload', onUnload);

    return () => {
      window.removeEventListener('unload', onUnload);
    };
  }, []);
}
